import { Col, Divider, Row } from 'antd';
import React, { useCallback } from 'react';
import { useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import {
  useGetVariantDetailQuery,
  useGetVariantHistoryQuery,
} from '../../api/variantApi';
import DetailCard from '../components/DetailCard';
import VariantUpdatePrimaryTranscript from '../components/VariantUpdatePrimaryTranscript';
import MainInterpretation from '../interpretation/index';
import Interpretation from '../interpretation/Interpretation';
import VariantChangelog from './VariantChangelog';
import VariantComments from './VariantComments';
import VariantHistory from './VariantHistory';

const Summary = () => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const snapshotId = Number(searchParams.get('interpretation_snapshot'));
  const isSnapshotRequest =
    Number.isInteger(snapshotId) && snapshotId > 0 && Boolean(params.vid);

  // Read the selected variant straight from the RTK Query cache (populated
  // by the workflow loader) rather than a Redux selector.
  const { data: variant } = useGetVariantDetailQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );

  // Read history straight from the query hook's cache rather than a selector
  const { data: variantHistory } = useGetVariantHistoryQuery(
    { variant_id: params.vid },
    { skip: !params.vid },
  );
  const varianSummaryValuesRenderer = useCallback(
    (label: string, value?: string) => {
      return (
        <>
          <Row>
            <Col flex={'35%'} className="variant-summary__field-label">
              {label}
            </Col>
            <Col flex={'65%'} className="variant-summary__field-value">
              {value}
            </Col>
          </Row>
          <Divider style={{ margin: '5px 0px' }} />
        </>
      );
    },
    [],
  );

  if (isSnapshotRequest) {
    return <MainInterpretation />;
  }

  return (
    <div className="variant-summary">
      <VariantUpdatePrimaryTranscript />
      <Row gutter={[8, 8]} className="workflow-stage-content-container">
        <Col flex={'auto'} className="workflow-stage-left-col">
          <DetailCard>
            <h3 style={{ marginBottom: 10 }}>Variant summary</h3>
            <Row
              className="variant-summary__section"
              gutter={[12, 12]}
              style={{ marginBottom: '1.5rem' }}
            >
              <Col flex={'50%'}>
                {varianSummaryValuesRenderer(
                  'Gene',
                  `${variant?.gene_symbol} (${variant?.gene_name})`,
                )}
                {varianSummaryValuesRenderer(
                  'Transcript',
                  `${variant?.transcript_external_id}${variant?.mane ? ` (${variant?.mane})` : ''}`,
                )}

                {varianSummaryValuesRenderer('cDNA', variant?.cdna)}
                {varianSummaryValuesRenderer(
                  'Variant Type',
                  variant?.variant_type, // new field
                )}
                {varianSummaryValuesRenderer(
                  'Protein change',
                  `${variant?.protein_change}/${variant?.protein_change_single_letter}`,
                )}
                {varianSummaryValuesRenderer(
                  'Consequence',
                  variant?.consequence,
                )}
              </Col>
              <Col flex={'50%'}>
                {varianSummaryValuesRenderer(
                  'Start-end (length)',
                  `${variant?.genomic_location_start} - ${variant?.genomic_location_end}(${variant?.length_calculated})`,
                )}
                {varianSummaryValuesRenderer(
                  'Chromosome',
                  variant?.chromosome_symbol,
                )}
                {varianSummaryValuesRenderer(
                  'Affected exon',
                  variant?.affected_exon,
                )}
                {varianSummaryValuesRenderer('gHGVS', variant?.hgvs_genomic)}
              </Col>
            </Row>

            <VariantComments />
          </DetailCard>
          <DetailCard>
            <h3>Variant history</h3>
            <VariantHistory />
            {variantHistory?.changelog &&
              variantHistory.changelog.length > 0 && (
                <VariantChangelog changelogs={variantHistory.changelog} />
              )}
          </DetailCard>
        </Col>
        {variant && <Interpretation variant={variant} showSnapshots />}
      </Row>
    </div>
  );
};

export default Summary;
